import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { lobbyOptionsSchema } from './lobby';
import { Profile, matchmakingPlayerSchema } from './shared';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';

export enum InvitationMessageType {
  INVITATION_QUERY = 'INVITATION_QUERY',
  INVITATION_QUERY_RESPONSE = 'INVITATION_QUERY_RESPONSE',
  INVITATION_JOIN = 'INVITATION_JOIN',
  INVITATION_IS_ALIAS = 'INVITATION_IS_ALIAS',
  INVITATION_ALIAS_CHANGED = 'INVITATION_ALIAS_CHANGED',
  INVITATION_JOIN_SUCCESS = 'INVITATION_JOIN_SUCCESS',
  INVITATION_JOIN_ERROR = 'INVITATION_JOIN_ERROR',
  INVITATION_STORE = 'INVITATION_STORE',
  INVITATION_STORE_SUCCESS = 'INVITATION_STORE_SUCCESS',
  INVITATION_STORE_ERROR = 'INVITATION_STORE_ERROR',
  INVITATION_MACHINE_HASH_QUERY = 'INVITATION_MACHINE_HASH_QUERY',
  INVITATION_MACHINE_HASH_QUERY_RESPONSE = 'INVITATION_MACHINE_HASH_QUERY_RESPONSE',
  INVITATION_UNSUBSCRIBE = 'INVITATION_UNSUBSCRIBE',
  INVITATION_UNSUBSCRIBE_SUCCESS = 'INVITATION_UNSUBSCRIBE_SUCCESS',
}

export type InvitationPlayerSchema = z.infer<typeof playerSchema>;
export type Invitation = z.infer<typeof invitationStorePayloadSchema>['invitation'];
// export type InvitationBody = z.infer<typeof invitationBodySchema>;

// Common schemas
const playerSchema = z.object({
  playerId: z.string(),
  displayName: z.string(),
  position: z.number().optional(),
});

const invitationBaseSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  linkExpiry: z.string(),
  createdAt: z.number().optional(),
  maxLinkUse: z.number().or(z.literal(-1)),
  authRequired: z.boolean(),
  invalid: z.boolean().default(false),
  invalidReasons: z
    .array(
      z.object({
        reason: z.string(),
        timestamp: z.number(),
      }),
    )
    .optional(),
  visibility: z.enum(['public', 'private']).default('public'),
  owner: z.object({
    avatar: z.string(),
    profileId: z.string(),
    username: z.string(),
  }),
  type: z.enum(['lobby', 'cafe']),
});

const lobbyManifestSchema = z.object({
  machineId: z.string(),
  defaultPlayerSettings: z.object({
    gamepad: z.boolean(),
    keyboard: z.boolean(),
    mouse: z.boolean(),
  }),
  regionName: z.string().optional(),
  lobbyOptions: lobbyOptionsSchema,
});

const cafeManifestSchema = z.object({
  players: z.array(matchmakingPlayerSchema),
  matchmakeToPublicMachine: z.boolean().optional(),
  matchmakingPreferences: z.record(z.string(), z.any()).optional(),
  queueStatus: z.enum(['idle', 'queued', 'matched', 'ready', 'failed', 'cancelled']).optional(),
  queueMessage: z.string().optional(),
  machineId: z.string().optional(),
  matchedAt: z.string().optional(),
  steamId: z.string().optional(),
  useSteamGuestLogin: z.boolean().optional(),
  softKickExpirationTime: z.number().optional(),

  // Geohash-based matchmaking fields (December 2025)
  campaignId: z.string().optional(), // Campaign slug for game-specific matching
  targetMachineId: z.string().optional(), // Direct machine targeting (admin/testing)
  searchPattern: z.array(z.string()).optional(), // Custom search pattern override
});

const gameRoomManifestSchema = z.object({
  userWhiteList: z.array(z.string()),
  rolesToGrantOnJoin: z.array(z.string()),
  profileId: z.string(),
});

export const invitationUnion = z.union([
  invitationBaseSchema.extend({
    type: z.literal('lobby'),
    manifest: lobbyManifestSchema,
  }),
  invitationBaseSchema.extend({
    type: z.literal('cafe'),
    manifest: cafeManifestSchema,
  }),
]);

// Payload schemas
const invitationQueryPayloadSchema = z.object({
  invitationId: z.string(),
  platform: z.nativeEnum(IdentityPlatform),
});

const invitationQueryPayloadResponseSchema = z.object({
  success: z.boolean(),
  invitationId: z.string(),
  invitation: invitationUnion.optional(),
  defaultAlias: z.string().optional(),
  defaultAliasPlatform: z.nativeEnum(IdentityPlatform).optional(),
});

const invitationJoinPayloadSchema = z.object({
  invitationId: z.string(),
  persistentId: z.string().optional(),
  displayName: z.string().optional(),
});

const invitationIsAliasPayloadSchema = z.object({
  invitations: z.array(invitationUnion),
  authId: z.string(),
  profile: z.custom<Profile>().optional(),
  defaultAlias: z.string().optional(),
  defaultAliasPlatform: z.nativeEnum(IdentityPlatform).optional(),
});

const invitationAliasChangedPayloadSchema = z.object({
  updatedInvitation: invitationUnion,
});

const invitationJoinSuccessPayloadSchema = z.object({
  position: z.number(),
  lobbyOptions: lobbyOptionsSchema.optional(),
  invitationPlayerId: z.string(),
  players: z.array(playerSchema),
  hostPeerId: z.string().optional(),
});

const invitationJoinErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const invitationStoreSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
  updatedInvitation: invitationUnion,
});

const invitationStoreErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const machineHashQueryPayloadSchema = z.object({
  machineIdHash: z.string(),
});

const machineHashQueryResponsePayloadSchema = z.object({
  success: z.boolean(),
  machineIdHash: z.string(),
  invitationId: z.string().optional(),
  invitation: invitationUnion.optional(),
});

const machineHashStorePayloadSchema = z.object({
  machineIdHash: z.string(),
  invitationId: z.string(),
});

const machineHashStoreSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
  machineIdHash: z.string(),
  invitationId: z.string(),
});

const machineHashStoreErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

export const invitationBodySchema = z.discriminatedUnion('type', [
  invitationBaseSchema.extend({
    type: z.literal('lobby'),
    manifest: lobbyManifestSchema,
  }),
  invitationBaseSchema.extend({
    type: z.literal('instant'),
    manifest: lobbyManifestSchema,
  }),
  invitationBaseSchema.extend({
    type: z.literal('gameRoom'),
    manifest: gameRoomManifestSchema,
  }),
]);

const invitationStorePayloadSchema = z.object({
  invitation: invitationUnion,
});

// Full message schemas
const invitationQuerySchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_QUERY),
  })
  .merge(invitationQueryPayloadSchema);

const invitationQueryResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_QUERY_RESPONSE),
  })
  .merge(invitationQueryPayloadResponseSchema);

const invitationJoinSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_JOIN),
  })
  .merge(invitationJoinPayloadSchema);

const invitationIsAliasSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_IS_ALIAS),
  })
  .merge(invitationIsAliasPayloadSchema);

const invitationAliasChangedSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_ALIAS_CHANGED),
  })
  .merge(invitationAliasChangedPayloadSchema);

const invitationJoinSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_JOIN_SUCCESS),
  })
  .merge(invitationJoinSuccessPayloadSchema);

const invitationJoinErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_JOIN_ERROR),
  })
  .merge(invitationJoinErrorPayloadSchema);

const invitationStoreSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_STORE),
  })
  .merge(invitationStorePayloadSchema);

const invitationStoreSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_STORE_SUCCESS),
  })
  .merge(invitationStoreSuccessPayloadSchema);

const invitationStoreErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_STORE_ERROR),
  })
  .merge(invitationStoreErrorPayloadSchema);

const machineHashQuerySchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_MACHINE_HASH_QUERY),
  })
  .merge(machineHashQueryPayloadSchema);

const machineHashQueryResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_MACHINE_HASH_QUERY_RESPONSE),
  })
  .merge(machineHashQueryResponsePayloadSchema);

// Add the unsubscribe payload schemas
const invitationUnsubscribePayloadSchema = z.object({
  invitationIds: z.array(z.string()),
});

const invitationUnsubscribeSuccessPayloadSchema = z.object({
  success: z.boolean(),
  invitationIds: z.array(z.string()),
});

// Add the full message schemas for unsubscribe
const invitationUnsubscribeSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_UNSUBSCRIBE),
  })
  .merge(invitationUnsubscribePayloadSchema);

const invitationUnsubscribeSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(InvitationMessageType.INVITATION_UNSUBSCRIBE_SUCCESS),
  })
  .merge(invitationUnsubscribeSuccessPayloadSchema);

export const invitationMessageSchema = z.discriminatedUnion('type', [
  invitationQuerySchema,
  invitationQueryResponseSchema,
  invitationJoinSchema,
  invitationIsAliasSchema,
  invitationAliasChangedSchema,
  invitationJoinSuccessSchema,
  invitationJoinErrorSchema,
  invitationStoreSchema,
  invitationStoreSuccessSchema,
  invitationStoreErrorSchema,
  machineHashQuerySchema,
  machineHashQueryResponseSchema,
  invitationUnsubscribeSchema,
  invitationUnsubscribeSuccessSchema,
]);

// Export all payload types
export type InvitationQueryPayload = z.infer<typeof invitationQueryPayloadSchema>;
export type InvitationQueryPayloadResponse = z.infer<typeof invitationQueryPayloadResponseSchema>;
export type InvitationJoinPayload = z.infer<typeof invitationJoinPayloadSchema>;
export type InvitationIsAliasPayload = z.infer<typeof invitationIsAliasPayloadSchema>;
export type InvitationAliasChangedPayload = z.infer<typeof invitationAliasChangedPayloadSchema>;
export type InvitationJoinSuccessPayload = z.infer<typeof invitationJoinSuccessPayloadSchema>;
export type InvitationJoinErrorPayload = z.infer<typeof invitationJoinErrorPayloadSchema>;
export type InvitationStorePayload = z.infer<typeof invitationStorePayloadSchema>;
export type InvitationStoreSuccessPayload = z.infer<typeof invitationStoreSuccessPayloadSchema>;
export type InvitationStoreErrorPayload = z.infer<typeof invitationStoreErrorPayloadSchema>;
export type MachineHashQueryPayload = z.infer<typeof machineHashQueryPayloadSchema>;
export type MachineHashQueryResponsePayload = z.infer<typeof machineHashQueryResponsePayloadSchema>;
export type MachineHashStorePayload = z.infer<typeof machineHashStorePayloadSchema>;
export type MachineHashStoreSuccessPayload = z.infer<typeof machineHashStoreSuccessPayloadSchema>;
export type MachineHashStoreErrorPayload = z.infer<typeof machineHashStoreErrorPayloadSchema>;
export type LobbyManifest = z.infer<typeof lobbyManifestSchema>;
export type GameRoomManifest = z.infer<typeof gameRoomManifestSchema>;

// Full message type
export type InvitationMessage = z.infer<typeof invitationMessageSchema>;

type PayloadType<T extends InvitationMessageType> = T extends InvitationMessageType.INVITATION_QUERY
  ? InvitationQueryPayload
  : T extends InvitationMessageType.INVITATION_QUERY_RESPONSE
    ? InvitationQueryPayloadResponse
    : T extends InvitationMessageType.INVITATION_JOIN
      ? InvitationJoinPayload
      : T extends InvitationMessageType.INVITATION_IS_ALIAS
        ? InvitationIsAliasPayload
        : T extends InvitationMessageType.INVITATION_ALIAS_CHANGED
          ? InvitationAliasChangedPayload
          : T extends InvitationMessageType.INVITATION_JOIN_SUCCESS
            ? InvitationJoinSuccessPayload
            : T extends InvitationMessageType.INVITATION_JOIN_ERROR
              ? InvitationJoinErrorPayload
              : T extends InvitationMessageType.INVITATION_STORE
                ? InvitationStorePayload
                : T extends InvitationMessageType.INVITATION_STORE_SUCCESS
                  ? InvitationStoreSuccessPayload
                  : T extends InvitationMessageType.INVITATION_STORE_ERROR
                    ? InvitationStoreErrorPayload
                    : T extends InvitationMessageType.INVITATION_MACHINE_HASH_QUERY
                      ? MachineHashQueryPayload
                      : T extends InvitationMessageType.INVITATION_MACHINE_HASH_QUERY_RESPONSE
                        ? MachineHashQueryResponsePayload
                        : T extends InvitationMessageType.INVITATION_UNSUBSCRIBE
                          ? InvitationUnsubscribePayload
                          : T extends InvitationMessageType.INVITATION_UNSUBSCRIBE_SUCCESS
                            ? InvitationUnsubscribeSuccessPayload
                            : never;

export function generateInvitationMessage<T extends InvitationMessageType>(type: T, payload: PayloadType<T>): InvitationMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case InvitationMessageType.INVITATION_QUERY:
      return invitationQuerySchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_QUERY_RESPONSE:
      return invitationQueryResponseSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_JOIN:
      return invitationJoinSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_IS_ALIAS:
      return invitationIsAliasSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_ALIAS_CHANGED:
      return invitationAliasChangedSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_JOIN_SUCCESS:
      return invitationJoinSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_JOIN_ERROR:
      return invitationJoinErrorSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_STORE:
      return invitationStoreSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_STORE_SUCCESS:
      return invitationStoreSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_STORE_ERROR:
      return invitationStoreErrorSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_MACHINE_HASH_QUERY:
      return machineHashQuerySchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_MACHINE_HASH_QUERY_RESPONSE:
      return machineHashQueryResponseSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_UNSUBSCRIBE:
      return invitationUnsubscribeSchema.parse({
        ...base,
        ...payload,
      });
    case InvitationMessageType.INVITATION_UNSUBSCRIBE_SUCCESS:
      return invitationUnsubscribeSuccessSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}

// Export the new payload types
export type InvitationUnsubscribePayload = z.infer<typeof invitationUnsubscribePayloadSchema>;
export type InvitationUnsubscribeSuccessPayload = z.infer<typeof invitationUnsubscribeSuccessPayloadSchema>;
